import { Box, Button, Link, MenuItem, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOfflineDetectContext } from "../../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from '../../../contexts/user-context/UserContext';
import { useTimezones } from '../../../queries/time-zones/UseTimezones';
import UserDataService from '../../../services/user-data-service/UserDataService';
import ChangePasswordForm from '../../change-password-form/ChangePasswordForm';
import FormInput from "../../forms/form-input/FormInput";
import FormPropertySection from '../../forms/form-property-section/FormPropertySection';
import FormProperty from '../../forms/form-property/FormProperty';
import FormSelect from "../../forms/form-select/FormSelect";
import { QRCodeSVG } from "qrcode.react";
import icon from "../../../assets/images/logo_square.png";
import { CheckCircleOutlined, Mail, Phone, Sms } from '@mui/icons-material';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AppNotificationTypeEnum } from "../../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../../contexts/app-notification-context/UseAppNotificationContext";
import { MfaSourceTypeEnum } from "../../../common/enums/MfaSourceTypeEnum";
import { RestError } from "../../../services/rest-service/RestService";
import { AuthenticationResultEnum } from "../../../common/enums/AuthenticationResultEnum";
import AccountSettingUtils, { AccountSettingsFormType, jobFunctions } from '../AccountSettingUtils';
import { UserRecipientFieldArrayType } from '../../user-recipient-list/UserRecipientFieldTypes';
import { RecipientTypeEnum } from '../../../common/enums/RecipientTypeEnum';
import Enumerable from 'linq';
import { getRecipientTypeName } from '../../../common/utilities/GetRecipientTypeName';
import NoData from '../../no-data/NoData';
import UserRecipientList from '../../user-recipient-list/UserRecipientList';
import TwoStepConfirmationDialog, { bindTwoStepConfirmationDialog, useTwoStepConfirmationDialogState } from '../../two-step-confirmation-dialog/TwoStepConfirmationDialog';
import { phoneNumberWithoutCountryCodeRegex } from '../../../common/regex/RegEx';

export type AccountProfileProps = {
    visible: boolean,
};

export type MfaSourceOption = {
    id: number,
    name: string
}

export type MfaInfo = {
    verified: boolean,
    secret: string,
    authenticationGuid?: string,
    lastOtp: string,
    otpLastVerified: string
}

export default function AccountProfile({ visible }: AccountProfileProps) {

    const [changePassword, setChangePassword] = useState(false);
    const [offlineState] = useOfflineDetectContext();
    const [userState] = useUserContext();
    const [,setAppNotificationState] = useAppNotificationContext();
    const disableConfirmationDialog = useTwoStepConfirmationDialogState();
    const isSSO = UserDataService.getIsSSO();
    const { t } = useTranslation();
    const theme = useTheme();

    // If MFA is required, then don't even present None as an option.
    const mfaSources: MfaSourceOption[] = [
        ...(userState.userSettings?.mfaRequired ? [] : [{ id: 0, name: "None"}]),
        { id: 1, name: "Email"},
        { id: 2, name: "Authenticator App"}
    ];

    // Temporary state for validation of MFA. Could/should this be moved to the AccountSettingsContext?
    // I'm thinking no since this state does not need
    const [mfaInfo, setMfaInfo] = useState<MfaInfo>({
        verified: userState.userSettings?.mfaSource === MfaSourceTypeEnum.Authenticator,
        secret: userState.userSettings?.mfaSecret,
        authenticationGuid: userState.userSettings?.authenticationGuid,
        lastOtp: "",
        otpLastVerified: userState.userSettings?.otpLastVerified ?? ""
    } as MfaInfo);
    
    const [loadingMfaInfo, setLoadingMfaInfo] = useState(false);
    const [mfaDetailsExpanded, setMfaDetailsExpanded] = useState(false);

    const formatQrData = useCallback((secret: string): string => {
        return `otpauth://totp/${encodeURI(userState.userSettings?.mfaAccount ?? "")}?secret=${secret ?? ""}&issuer=eLynx%20Technologies`
    }, [userState.userSettings?.mfaAccount]);

    // If we are configured to use Authenticator, then initialize our qrData with the correct info to
    // recreate the same QR Code that was created initially, otherwise we can leave the QR data blank
    // and only fill it in if and when a user chooses to use Authenticator.
    const [qrData, setQrData] = useState(
        userState.userSettings?.mfaSource === MfaSourceTypeEnum.Authenticator ?
        formatQrData(userState.userSettings.mfaSecret) :
        ""
    );

    const {
        watch,
        setValue,
    } = useFormContext<AccountSettingsFormType>();    

    const onChangePassword = () => {
        setChangePassword(true);
    };
    const onCloseChangePassword = () => {
        setChangePassword(false);
    };

    const {data: timezones} = useTimezones();

    const [
        selectedSource,
        email, 
        authenticationGuid,
        watchRecipients
     ] = watch([
        "mfaSource",
        'email',
        "authenticationGuid",
        'recipients'
    ]);


    const {
        fields,
        //append,
        //remove,
        update
    } = useFieldArray<AccountSettingsFormType, "recipients", "fieldID">({ name: "recipients", keyName: "fieldID" });


    const recipients: UserRecipientFieldArrayType =
        fields.map((field, index) => {
            return {
                ...field,
                ...watchRecipients[index],
                recipientKey: `recipients.${index}`
            };
        });

    const emailRecipients = Enumerable
        .from(recipients)
        .where(r => r.type === RecipientTypeEnum.Email)
        .orderBy(a => a.to)
        .toArray();
    const smsRecipients = Enumerable
        .from(recipients)
        .where(r => r.type === RecipientTypeEnum.Text)
        .orderBy(a => a.to)
        .toArray();
    const voiceRecipients = Enumerable
        .from(recipients)
        .where(r => r.type === RecipientTypeEnum.Voice)
        .orderBy(a => a.to)
        .toArray();

    async function onDisableRecipient(recipientKey: string, disable: boolean) {

        const index = recipients.findIndex(r => r.recipientKey === recipientKey);

        if (index > -1) {

            const recipient = fields[index];

            if (disable && !recipient.disabledTimestamp) {

                const recipientTypeName = getRecipientTypeName(recipient.type);

                const result = await disableConfirmationDialog.open({
                    title: t("userEditor.steps.alarmNotifications.disableConfirmation.title"),
                    explanation: (
                        <Stack direction="column" spacing={2}>
                            <Typography>
                                {t("userEditor.steps.alarmNotifications.disableConfirmation.explanation1", { recipientType: recipientTypeName, recipientTo: recipient.to })}
                            </Typography>
                            <Typography sx={{
                                fontWeight: "bold"
                            }}>
                                {t("userEditor.steps.alarmNotifications.disableConfirmation.explanation2", { recipientType: recipientTypeName, recipientTo: recipient.to })}
                            </Typography>
                        </ Stack>
                    ),
                    confirmationText: t("userEditor.steps.alarmNotifications.disableConfirmation.confirmationText", { recipientType: recipientTypeName })
                });

                if (result.reason === "ok") {
                    update(index, {
                        ...recipient,
                        disabled: disable
                    });
                }
            } else {
                update(index, {
                    ...recipient,
                    disabled: disable
                });
            }
        }
    }

    const onNewSecret = useCallback(async () => {
        if (!loadingMfaInfo) {
            console.log("Requesting new secret...");
            try {
                setLoadingMfaInfo(true);
                const data = await UserDataService.requestAuthenticatorSecret();
                setMfaInfo({
                    ...mfaInfo,
                    authenticationGuid: data.authenticationGuid,
                    secret: data.secret,
                    verified: false,
                    lastOtp: "",
                    otpLastVerified: ""
                } as MfaInfo);
                setValue("otp", "", { shouldValidate: true });
                setQrData(formatQrData(data.secret));
            }
            catch (error) {

                const restError = error as RestError;

                if (restError.response?.status === 400) {
                    setAppNotificationState({
                        notificationType: AppNotificationTypeEnum.Error,
                        show: true,
                        message: restError.response?.data?.message,
                        hideContactInfo: true
                    });
                } else {
                    setAppNotificationState({
                        notificationType: AppNotificationTypeEnum.Error,
                        show: true,
                        error: restError
                    });
                }
            }
            finally{
                setLoadingMfaInfo(false);
            }
        }
    }, [mfaInfo,
        setValue,
        setQrData,
        formatQrData,
        setAppNotificationState,
        loadingMfaInfo
    ]);

    const onMfaSourceChanged = (source: MfaSourceTypeEnum) => {
        //console.log("onMfaSourceChanged: ", source);
        if (source === MfaSourceTypeEnum.Authenticator && !mfaInfo.verified) {
            // if we already have an Authenticator secret (because MFA Authenitcator access has
            // been configured on another customer), then don't request a new secret. Simply
            // update our state so that we display the same QR code. We need to check the
            // userState.userSettings here as that would have been established during the
            // login process. The mfaInfo.secret, however, may contain a secret that we
            // generated here by choosing Authenticator as a source.
            if (userState.userSettings?.mfaSecret) {
                // It's possible that the user could have hit Request New Secret (which would update mfaInfo.authenticationGuid),
                // switched to Email, then back to Authenticator. In that case, we want to keep the new secret info, which
                // if we get here means that it hasn't been verified yet. To catch this case, we can compare the
                // mfaInfo.authenticationGuid to the userState.userSettings.authenticationGuid, which will have the
                // authentication guid from before any changes were made.
                console.log("Initializing QR with pre-existing auth data. Setting verified to", userState.userSettings?.authenticationGuid === mfaInfo?.authenticationGuid);

                setMfaInfo({
                    ...mfaInfo,
                    verified: userState.userSettings?.authenticationGuid === mfaInfo?.authenticationGuid
                });

                setQrData(formatQrData(userState.userSettings?.mfaSecret));

            } else if (!mfaInfo.secret) {
                onNewSecret();
            }
        } else if (mfaInfo.otpLastVerified) {
            setMfaInfo({
                ...mfaInfo,
                otpLastVerified: ""
            });
        }

        // When the auth source changes, always reset the OTP and rerun the validation to ensure
        // that we keep everything in sync properly and don't have the OTP field hidden, but
        // signaling the form as being invalid and preventing the ability to save.
        // NOTE: The delay here is a temporary workaround for the case when switching back to
        // ensure the validation doesn't occur before the underlying value has updated.
        // According to the RHF docs, you're not supposed to put a value from the watch into a
        // useEffect, so I'm not exactly sure what the correct way of doing this is yet.
        setTimeout(() => {
            setValue("otp", "", {shouldValidate: true });
        }, 100);
    }

    const validateOtp = async (otp: string): Promise<boolean | string> => {
        //if (user?.mfaSource !== MfaSourceTypeEnum.Authenticator || mfaInfo?.verified) {
        if (selectedSource !== MfaSourceTypeEnum.Authenticator || mfaInfo?.verified) {
            return true;
        }

        if (otp === undefined || otp === null || otp.length === 0) {
            return t('profile.otpVerificationRequired') as string;
        }

        if (mfaInfo?.lastOtp === otp)
        {
            return false;
        }

        if (isNaN(Number(otp))) {
            return t('profile.otpNotNumeric') as string;
        }

        if (otp.length < 6) {
            return t('profile.otpTooShort') as string;
        }

        const result = await UserDataService.validateOtp(otp, mfaInfo?.authenticationGuid);
        const success = result === AuthenticationResultEnum.Success;

        setMfaInfo({
            ...mfaInfo,
            verified: success,
            lastOtp: otp
        } as MfaInfo);

        return success ? success : t('profile.otpInvalid') as string;
    }

    const toggleExpanded = () => {
        setMfaDetailsExpanded(!mfaDetailsExpanded);
    }

    useEffect(() => {
        if (mfaInfo.authenticationGuid && authenticationGuid !== mfaInfo.authenticationGuid) {
            setValue('authenticationGuid', mfaInfo.authenticationGuid)
        }
    }, [
        authenticationGuid,
        mfaInfo.authenticationGuid,
        setValue,
    ])

    return visible ?
        <>

            <FormPropertySection label={t('profile.accountInfo')}>
                <FormProperty>
                <FormInput
                        id="email"
                        testId="email"
                        name="email"
                        ariaLabel="login and contact"
                        label={t('profile.emailLabel')}
                        rules={{
                            pattern: {
                                value: /^[A-Za-z0-9+_-][A-Za-z0-9.+_-]{0,63}@[A-Za-z0-9.-]{1,61}\.[A-Za-z]{2,}$/,
                                message: t('profile.invalidEmail')
                            },
                            required: true
                        }}
                        helperText=" "
                        readOnly={offlineState.offline}
                        sx={{width: '100%'}}
                    />
                </FormProperty>

                <FormProperty>
                <FormInput
                        id="phone"
                        testId="phone"
                        ariaLabel="phone number"
                        name="phone"
                        label={t('profile.phone')}
                        rules={{
                            pattern: {
                                // Bug 52761: DOC - Add User Error
                                value: phoneNumberWithoutCountryCodeRegex,
                                //value: /^(1-)?\d{3}-\d{3}-\d{4}$/,
                                //value: /^\(\d{3}\) ?\d{3}( |-)?\d{4}|^\d{3}( |-)?\d{3}( |-)?\d{4}/,
                                message: t('profile.invalidPhone')
                            }
                        }}
                        helperText=" "
                        readOnly={offlineState.offline}
                        sx={{width: '100%'}}
                    >
                        {/* <InputMask mask="999-999-9999" maskPlaceholder=" " alwaysShowMask={true} /> */}
                    </FormInput>
                </FormProperty>
            </FormPropertySection>

            {
                    // !isSSO && !(user?.loginName === 'demo@elynxtech.com') &&
                    !isSSO && !(email === 'demo@elynxtech.com') &&
                    <FormPropertySection label={
                        <Link style={{ cursor: 'pointer' }}
                            aria-label="change password"
                            onClick={() => onChangePassword()}>
                            {t("profile.changePassword")}
                        </Link>

                    } ><></>
                    </FormPropertySection>
                }


            <FormPropertySection label={t('profile.userInfo')}>
                <FormProperty>
                    <FormInput
                        id="company"
                        testId="company"
                        ariaLabel="company name"
                        name="company"
                        label={t('profile.company')}
                        readOnly={offlineState.offline}
                        sx={{width: '100%'}}
                    />

                </FormProperty>

                <FormProperty>
                    <FormInput
                        id="jobtitle"
                        testId="jobtitle"
                        ariaLabel="job title"
                        name="jobTitle"
                        label={t('profile.jobTitle')}
                        readOnly={offlineState.offline}
                        sx={{width: '100%'}}
                    />

                </FormProperty>

                <FormProperty>
                    <FormSelect
                        id="jobfunction"
                        testId="jobfunction"
                        ariaLabel="job function"
                        name="jobFunction"
                        label={t('profile.jobFunction')}
                        readOnly={offlineState.offline}
                        sx={{width: '100%'}}
                    >
                        {jobFunctions.map((value, index) =>
                            <MenuItem key={`jf-${index}`} value={value}>
                                {value}
                            </MenuItem>)}
                    </FormSelect>
                </FormProperty>

                <FormProperty>
                    <FormSelect
                        id="timezone"
                        testId="timezones"
                        ariaLabel="timezone"
                        name="timezone"
                        label={t('profile.timeZone')}
                        readOnly={offlineState.offline}
                        sx={{width: '100%'}}
                    >
                        {timezones?.map((o, index) =>
                            <MenuItem key={`tz-${index}`} value={o.id}>
                                {o.name}
                            </MenuItem>)}
                    </FormSelect>

                </FormProperty>
            </FormPropertySection>

            { !isSSO && userState?.userSettings?.mfaEnabled && (
                <FormPropertySection label={t('profile.multifactorAuthentication')}>
                    <FormProperty>
                        <FormSelect
                            id="mfaSource"
                            testId="mfaSource"
                            ariaLabel="mfaSource"
                            name="mfaSource"
                            label={t('profile.authenticationSource')}
                            readOnly={offlineState.offline}
                            onChange={(e) => onMfaSourceChanged(Number(e.target.value) as MfaSourceTypeEnum)}
                            sx={{width: '100%'}}
                        >
                            {mfaSources.map(value =>
                                <MenuItem key={`mfa-${value.id}`} value={value.id}>
                                    {value.name}
                                </MenuItem>)}
                        </FormSelect>
                    </FormProperty>

                    <FormProperty
                        sx={{
                            display: selectedSource === MfaSourceTypeEnum.Authenticator && mfaInfo.secret ? 'flex' : 'none',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                        <Typography variant='subtitle1' color='textSecondary' gutterBottom sx={{
                            fontWeight: 'bold'
                        }}>
                            <Box>
                                {t('profile.qrCodeInstruction')}
                            </Box>
                        </Typography>
                        <Box>
                            <QRCodeSVG
                                id="qrMfa"
                                size={256}
                                value={qrData}
                                bgColor="white"
                                fgColor='#293955'       // can't use textSecondary here. QRCode element doesn't translate it in properly. Also, theme.palette.text.secondary isn't right either.
                                imageSettings={{
                                    src: icon,          // eLynx logo in the QR :)
                                    height: 256 * .1,   // Because the QRCode component sometimes throws errors if you don't explicitly set these
                                    width: 256 * .1,    // Because the QRCode component sometimes throws errors if you don't explicitly set these
                                    excavate: true
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                marginTop: theme.spacing(1),
                                marginBottom: theme.spacing(1)
                            }}>
                            <Button variant="outlined"
                                onClick={() => onNewSecret()}>
                                {t('profile.requestNewSecret')}
                            </Button>
                        </Box>
                    </FormProperty>

                    <FormProperty
                        sx={{
                            display: selectedSource === MfaSourceTypeEnum.Authenticator && mfaInfo.secret ? 'flex' : 'none',
                            flexDirection: 'column',
                            alignItems: 'flex-start'
                        }}>
                        <Typography variant='subtitle2' color='textSecondary'
                            sx={{
                                width: '100%'
                            }}>
                            <Box
                                sx={{
                                    display: mfaInfo?.verified ? 'none' : 'flex',
                                    alignItems: 'center'
                                }}>
                                    {t('profile.authenticatorValidationInfo')}
                            </Box>
                            <FormInput
                                name="otp"
                                id="otp"
                                testId="otp"
                                //format="000000"
                                //numeric   // This converts input to numbers, which is ok, but it can chop off leading zeros. Maybe this can work with a proper format?
                                //decimals={0}
                                label={t("profile.verifyOtp")}
                                rules={{
                                    validate: (value: string) => selectedSource !== MfaSourceTypeEnum.Authenticator || validateOtp(value)
                                }}
                                sx={{
                                    display: mfaInfo?.verified ? 'none' : 'flex',
                                    marginTop: theme.spacing(1),
                                    marginBottom: theme.spacing(1),
                                    maxWidth: '200px'
                                }}>
                            </FormInput>
                            <Box
                                sx={{
                                    display: mfaInfo?.verified ? 'flex' : 'none',
                                    alignItems: 'center'
                                }}>
                                    <CheckCircleOutlined sx={{color: 'green', marginRight: theme.spacing(1)}}></CheckCircleOutlined>{t('profile.authenticatorValidationSuccess')}
                            </Box>
                            <Box
                                sx={{
                                    display: mfaInfo?.verified && mfaInfo.otpLastVerified ? 'flex' : 'none',
                                    color: 'green',
                                    fontStyle: 'italic'
                                }}>
                                {AccountSettingUtils.getOtpLastVerifiedText(mfaInfo.otpLastVerified)}
                            </Box>
                            <Box
                                sx={{
                                    display: mfaInfo?.verified && userState.userSettings?.authenticationGuid !== mfaInfo?.authenticationGuid ? 'flex' : 'none',
                                    marginTop: theme.spacing(1)
                                }}>
                                {t('profile.authenticatorNotPersisted')}
                            </Box>
                            <Box
                                sx={{
                                    marginTop: theme.spacing(1)
                                }}>
                                <Button
                                    sx={{
                                        //color: theme => theme.palette.getContrastText(theme.palette.background.paper)
                                        color: "text.primary"
                                    }}

                                    variant="text"
                                    onClick={toggleExpanded}
                                    style={{ marginLeft: "auto" }}
                                    endIcon={<ExpandMoreIcon sx={{
                                        transform: !mfaDetailsExpanded ? "rotate(0deg)" : "rotate(180deg)",
                                        transition: theme.transitions.create('transform', {
                                            duration: theme.transitions.duration.shortest,
                                        })
                                    }} />}
                                >
                                    {t('profile.manualAuthInfo')}
                                </Button>
                            </Box>
                            <Box
                                sx={{
                                    display: mfaDetailsExpanded ? 'flex' : 'none',
                                    flexDirection: 'column'
                                }}>
                                <TextField
                                    variant="filled"
                                    id="mfaAccount"
                                    name="mfaAccount"
                                    data-testid="mfaAccount"
                                    aria-label="mfaAccount"
                                    label={t('profile.mfaAccount')}
                                    value={userState?.userSettings?.mfaAccount || ""}
                                    sx={{
                                        marginTop: theme.spacing(1),
                                        width: '100%',
                                    }}
                                    slotProps={{
                                        input: {
                                            readOnly: true
                                        },

                                        inputLabel: {
                                            shrink: true
                                        }
                                    }} />
                                <TextField
                                    variant="filled"
                                    id="mfaAccount"
                                    name="mfaAccount"
                                    data-testid="mfaAccount"
                                    aria-label="mfaAccount"
                                    label={t('profile.mfaSecret')}
                                    value={mfaInfo?.secret || ""}
                                    sx={{
                                        marginTop: theme.spacing(1),
                                        width: '100%',
                                    }}
                                    slotProps={{
                                        input: {
                                            readOnly: true
                                        },

                                        inputLabel: {
                                            shrink: true
                                        }
                                    }} />

                                {/* <FormInput
                                    name="mfaAccount"
                                    id="mfaAccount"
                                    testId="mfaAccount"
                                    label="Account"
                                    readOnly={true}
                                    sx={{width: '90%', marginTop: theme.spacing(1)}} />
                                <FormInput
                                    name="mfaSecret"
                                    id="mfaSecret"
                                    testId="mfaSecret"
                                    label="Secret"
                                    readOnly={true}
                                    sx={{width: '90%', marginTop: theme.spacing(1)}} /> */}
                            </Box>
                            <Box
                                sx={{
                                    marginTop: theme.spacing(2)
                                }}>
                            </Box>
                        </Typography>
                    </FormProperty>

                    {/* <FormProperty>
                        <div>Render Count: {renderCount}</div>
                        <div>Selected source: {selectedSource}</div>
                    </FormProperty> */}

                    {/* <FormProperty>
                        <div>UserSettings...</div>
                        <div>MFA Required: {userState?.userSettings?.mfaRequired ? 'true' : 'false'}</div>
                        <div>MFA Enabled: {userState?.userSettings?.mfaEnabled ? 'true' : 'false'}</div>
                        <div>UserSettings Secret: {userState?.userSettings?.mfaSecret}</div>
                        <div>guid: {userState?.userSettings?.authenticationGuid}</div>
                        <div>Other...</div>
                        <div>MFAInfo Secret: {mfaInfo.secret}</div>
                        <div>MFAInfo guid: {mfaInfo.authenticationGuid}</div>
                        <div>isSSO: {isSSO ? 'true' : 'false'}</div>

                    </FormProperty> */}

                </FormPropertySection>
            )}


            {
            !offlineState.offline && 
            <FormPropertySection label={t('objectTypeNames.recipients')}>

            <Stack
                direction="column"
                spacing={1}
                sx={{
                    flex: "1 1 auto",
                    paddingLeft: 1,

                    //overflowY: "auto"
                    paddingRight: 1
                }}>                

            {recipients.length === 0 &&
                    <NoData
                        title={t("userEditor.steps.alarmNotifications.noRecipients.title")}
                        text={t("userEditor.steps.alarmNotifications.noRecipients.text")}
                    />
                }

                {emailRecipients.length > 0 &&
                    <UserRecipientList
                        title={t("userEditor.steps.alarmNotifications.emailNotifications")}
                        icon={<Mail />}
                        recipients={emailRecipients}
                        onDisable={onDisableRecipient}
                        hideUsage={true}
                    />
                }

                {smsRecipients.length > 0 &&
                    <UserRecipientList
                        title={t("userEditor.steps.alarmNotifications.smsNotifications")}
                        icon={<Sms />}
                        recipients={smsRecipients}
                        onDisable={onDisableRecipient}
                        hideUsage={true}
                    />
                }

                {voiceRecipients.length > 0 &&
                    <UserRecipientList
                        title={t("userEditor.steps.alarmNotifications.voiceNotifications")}
                        icon={<Phone />}
                        recipients={voiceRecipients}
                        onDisable={onDisableRecipient}
                        hideUsage={true}
                    />
                }

            </Stack>
                
            </FormPropertySection>
            }

            <ChangePasswordForm open={changePassword} onClose={onCloseChangePassword} />
            <TwoStepConfirmationDialog {...bindTwoStepConfirmationDialog(disableConfirmationDialog)} /> 
        </>
        : null;
}
